import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  isAzureADAvailable, 
  initiateAzureADLogin
} from '../utils/azureAuth';

// Pre-defined test tokens for easy login during development
const TEST_USERS = {
  'Scott (Organizer)': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJzY290dC5tdXJjaEBjcmVkaXRzYWZlLmNvbSIsIm5hbWUiOiJTY290dCBNdXJjaCIsImVtYWlsIjoic2NvdHQubXVyY2hAY3JlZGl0c2FmZS5jb20iLCJyb2xlcyI6WyJPUkdBTklaRVIiXSwiZXhwIjo5OTk5OTk5OTk5fQ.x7A8LQf5zNqF8g7O_xrZ-8sYpK2L-xQw4bU6aGk7N5E',
  'Alice (Participant)': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJhbGljZUBzdHVkZW50LnVuaXZlcnNpdHkuZWR1IiwibmFtZSI6IkFsaWNlIFN0dWRlbnQiLCJlbWFpbCI6ImFsaWNlQHN0dWRlbnQudW5pdmVyc2l0eS5lZHUiLCJyb2xlcyI6WyJwYXJ0aWNpcGFudCJdLCJleHAiOjk5OTk5OTk5OTl9.9f2Rv5hgwM3yxkQoLU0rLJf3m7b6lVp8mQo3YwKzN4E',
  'Charlie (Developer)': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJjaGFybGllLmRldkBzdHVkZW50LnVuaXZlcnNpdHkuZWR1IiwibmFtZSI6IkNoYXJsaWUgRGV2ZWxvcGVyIiwiZW1haWwiOiJjaGFybGllLmRldkBzdHVkZW50LnVuaXZlcnNpdHkuZWR1Iiwicm9sZXMiOlsicGFydGljaXBhbnQiXSwiZXhwIjo5OTk5OTk5OTk5fQ.uVgJONGJOi0WOfbWNQK0yI4C5cjm2VJcXJe8p8sYwgM',
  'Diana (Designer)': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJkaWFuYS5kZXNpZ25AZnJlZWxhbmNlci5jb20iLCJuYW1lIjoiRGlhbmEgRGVzaWduZXIiLCJlbWFpbCI6ImRpYW5hLmRlc2lnbkBmcmVlbGFuY2VyLmNvbSIsInJvbGVzIjpbInBhcnRpY2lwYW50Il0sImV4cCI6OTk5OTk5OTk5OX0.6K4-Xa7QJSHfpJnP6v3d8Gf4L8mVhTRNc2iQy9WzXkE',
  'Erik (ML Engineer)': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJlcmlrLm1sQHRlY2hjb3JwLmNvbSIsIm5hbWUiOiJFcmlrIE1MIEVuZ2luZWVyIiwiZW1haWwiOiJlcmlrLm1sQHRlY2hjb3JwLmNvbSIsInJvbGVzIjpbInBhcnRpY2lwYW50Il0sImV4cCI6OTk5OTk5OTk5OX0.8rLj5qK9mZNhGxYp4sVtF3cE2uPnTmAi7wRdBkOv6XE'
};

const Login: React.FC = () => {
  const [customToken, setCustomToken] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [azureADAvailable, setAzureADAvailable] = useState<boolean>(false);
  const [checkingAzureAD, setCheckingAzureAD] = useState<boolean>(true);
  const { login } = useAuth();

    // Check if Azure AD is available on component mount
  useEffect(() => {
    checkAzureADAvailability();
  }, []);

  const checkAzureADAvailability = async () => {
    setCheckingAzureAD(true);
    try {
      const available = await isAzureADAvailable();
      setAzureADAvailable(available);
    } catch (error) {
      console.warn('Could not check Azure AD availability:', error);
      setAzureADAvailable(false);
    } finally {
      setCheckingAzureAD(false);
    }
  };

  const handleAzureADLogin = async () => {
    setLoading(true);
    setError('');
    
    try {
      await initiateAzureADLogin();
      // Note: This will redirect to Azure AD, so code after this won't execute
    } catch (err: any) {
      setError(err.message || 'Failed to start Azure AD login');
      setLoading(false);
    }
  };

  const handleQuickLogin = async (userKey: string, token: string) => {
    setLoading(true);
    setError('');
    
    try {
      // Extract email from token for display
      const payload = JSON.parse(atob(token.split('.')[1]));
      login(payload.email, token);
    } catch (err) {
      console.error('Failed to parse JWT token:', err);
      setError('Invalid token format');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomLogin = async () => {
    if (!customToken.trim()) {
      setError('Please enter a token');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Extract email from token
      const payload = JSON.parse(atob(customToken.split('.')[1]));
      login(payload.email, customToken);
    } catch (err) {
      console.error('Failed to parse custom JWT token:', err);
      setError('Invalid token format');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      background: 'linear-gradient(180deg, #ffffff 0%, #f7f7f7 100%)'
    }}>
      <div className="card" style={{ maxWidth: '500px', width: '100%', margin: '20px' }}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <img 
            src="/InnovationDayLogo2026.png" 
            alt="Creditsafe Innovation Day" 
            style={{ height: '120px', maxWidth: '100%' }}
          />
        </div>
        
        <h2 style={{ textAlign: 'center', marginBottom: '30px' }}>
          Login to Hackathon Manager
        </h2>
        
        {error && (
          <div className="error">{error}</div>
        )}
        
        {/* Azure AD availability check loading */}
        {checkingAzureAD && (
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '30px',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
          }}>
            <div className="loading-spinner" style={{ marginBottom: '15px' }}></div>
            <p style={{ fontSize: '14px', color: '#666', margin: '0' }}>
              Checking authentication services...
            </p>
            <p style={{ fontSize: '12px', color: '#999', margin: '5px 0 0 0' }}>
              This may take a moment if the server is starting up
            </p>
          </div>
        )}
        
        {/* Azure AD Login Section */}
        {!checkingAzureAD && azureADAvailable && (
          <div style={{ marginBottom: '30px' }}>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
              Sign in with your Creditsafe account:
            </p>
            
            <button
              onClick={handleAzureADLogin}
              disabled={loading}
              className="btn"
              style={{ 
                width: '100%', 
                padding: '15px',
                backgroundColor: '#0078d4',
                color: 'white',
                border: 'none',
                marginBottom: '10px'
              }}
            >
              {loading ? 'Redirecting to Azure AD...' : 'Sign in with Microsoft'}
            </button>
            
            <p style={{ fontSize: '12px', color: '#666', textAlign: 'center' }}>
              You'll be redirected to Microsoft to sign in with your work account
            </p>
          </div>
        )}
        
        {/* Separator only when both Azure AD and development sections are shown */}
        {!checkingAzureAD && azureADAvailable && process.env.NODE_ENV === 'development' && (
          <hr style={{ margin: '30px 0', border: '1px solid #eee' }} />
        )}
        
        {/* Quick Login Section - Only show in development mode */}
        {!checkingAzureAD && process.env.NODE_ENV === 'development' && (
          <div style={{ marginBottom: '30px' }}>
            <h3>{azureADAvailable ? 'Development Login' : 'Quick Login (Test Users)'}</h3>
            <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
              {azureADAvailable 
                ? 'For development and testing purposes:' 
                : 'Select a test user to quickly access the application:'
              }
            </p>
            
            <div style={{ display: 'grid', gap: '10px' }}>
              {Object.entries(TEST_USERS).map(([userKey, token]) => {
                // Determine the role description
                let roleDescription: string;
                if (userKey.includes('Organizer')) {
                  roleDescription = '👑 Can create events';
                } else if (userKey.includes('Judge')) {
                  roleDescription = '⚖️ Can judge submissions';
                } else {
                  roleDescription = '👤 Can create teams';
                }
                
                return (
                  <button
                    key={userKey}
                    onClick={() => handleQuickLogin(userKey, token)}
                    disabled={loading}
                    className="btn"
                    style={{ textAlign: 'left', padding: '15px' }}
                  >
                    <strong>{userKey}</strong>
                    <br />
                    <small style={{ opacity: 0.8 }}>
                      {roleDescription}
                    </small>
                  </button>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Custom Token Login Section - Only show in development mode */}
        {!checkingAzureAD && process.env.NODE_ENV === 'development' && (
          <>
            <hr style={{ margin: '30px 0', border: '1px solid #eee' }} />
            
            <div>
              <h3>Custom Token Login</h3>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '15px' }}>
                Or paste your own JWT token:
              </p>
              
              <div className="form-group">
                <textarea
                  value={customToken}
                  onChange={(e) => setCustomToken(e.target.value)}
                  placeholder="Paste your JWT token here..."
                  rows={4}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
              </div>
              
              <button
                onClick={handleCustomLogin}
                disabled={loading || !customToken.trim()}
                className="btn"
                style={{ width: '100%' }}
              >
                {loading ? 'Logging in...' : 'Login with Custom Token'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Login;
